/**
 * Single source of truth for backend URLs in the connector service.
 *
 * The app previously resolved its API host in nine different places using two
 * different env vars (`VITE_API_URL`, `VITE_API_BASE`) and two different
 * hard-coded fallbacks (`localhost:8787`, `retailsync-api.abisoftsinc.workers.dev`).
 * Different pages therefore talked to different backends, and the import wizard
 * called `/api/v1/connector/import` on a host where that route did not exist.
 *
 * Both env var names are still honoured so an existing deployment config keeps
 * working — only the resolution lives here now.
 */

const env = ((import.meta as any).env || {}) as Record<string, string | undefined>

/**
 * Fallback MUST be the custom API domain, not the workers.dev host.
 *
 * `retailsync-api.abisoftsinc.workers.dev` strips the `x-tenant-id` header at the
 * edge, so every tenant-scoped endpoint answered
 * `400 {"error":"Tenant context required for license validation"}`:
 * Sync Monitoring and the Provider Marketplace were both dead in production for
 * this reason. `api.retailsync.africa` is the only host that forwards the tenant
 * header, so it is the safe default.
 */
const DEFAULT_API_ORIGIN = 'https://api.retailsync.africa'

function normalizeOrigin(value: string | undefined): string {
  if (!value) return DEFAULT_API_ORIGIN
  return value
    .trim()
    .replace(/\/+$/, '')
    // Tolerate a base that already includes the API path (/api or /api/v1).
    .replace(/\/api(\/v1)?$/, '')
}

/** e.g. https://retailsync-api.abisoftsinc.workers.dev */
export const API_ORIGIN = normalizeOrigin(env.VITE_API_URL || env.VITE_API_BASE)

/** e.g. https://retailsync-api.abisoftsinc.workers.dev/api/v1 */
export const API_URL = `${API_ORIGIN}/api/v1`

/** e.g. https://retailsync-api.abisoftsinc.workers.dev/api/v1/connector */
export const CONNECTOR_API_URL = `${API_URL}/connector`

/**
 * Auth headers every connector call needs — the API resolves the tenant from
 * either the Bearer token or `x-tenant-id`, and the connector endpoints require
 * one of them.
 */
export function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || ''
  const tenantId = localStorage.getItem('tenant_id') || sessionStorage.getItem('tenant_id') || ''
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
  }
}

/** The tenant-admin app is where a tenant activates/renews a license. */
export const LICENSE_ACTION_URL = env.VITE_TENANT_ADMIN_SIGNUP_URL || 'https://app.retailsync.africa/login'

/**
 * A failed API call, carrying the license payload the API returns for 402s:
 * `{ error, code: 'CREATE_TRIAL' | 'TRIAL_EXPIRED_RENEW' | 'INVALID_LICENSE', action, actionLabel }`.
 */
export class ApiError extends Error {
  status: number
  code?: string
  action?: string
  actionLabel?: string
  /** True when the tenant's license blocks the call (HTTP 402 / license codes). */
  licenseRequired: boolean

  constructor(message: string, status: number, payload: Record<string, any> = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = payload.code
    this.action = payload.action
    this.actionLabel = payload.actionLabel
    this.licenseRequired = status === 402 || [
      'CREATE_TRIAL', 'TRIAL_EXPIRED_RENEW', 'INVALID_LICENSE', 'TRIAL_LIMIT_EXCEEDED', 'FEATURE_NOT_AVAILABLE',
    ].includes(String(payload.code || ''))
  }
}

/** Parse a fetch Response, throwing a descriptive ApiError (never a bare status). */
export async function readApiResponse<T = any>(res: Response): Promise<T> {
  const payload = await res.json().catch(() => null)

  if (!res.ok) {
    // A license 402 means the request never reached the route — say so plainly
    // instead of surfacing "API error: 402".
    const fallback = res.status === 402
      ? 'License required for this workspace.'
      : res.status === 401
        ? 'Session expired — sign in again.'
        : `Request failed (${res.status})`
    throw new ApiError(payload?.error || payload?.message || fallback, res.status, payload || {})
  }

  return (payload ?? {}) as T
}

/** Human-readable explanation of a license rejection, for banners/call-to-actions. */
export function describeLicenseError(err: unknown): { message: string; actionLabel: string } {
  const apiError = err instanceof ApiError ? err : null
  const code = apiError?.code
  const message = code === 'CREATE_TRIAL'
    ? 'This workspace has no active license or trial. Activate a plan to use the connector.'
    : code === 'TRIAL_EXPIRED_RENEW'
      ? 'The trial for this workspace has expired. Renew to continue syncing.'
      : apiError?.message || 'License required for this workspace.'
  return { message, actionLabel: apiError?.actionLabel || (code === 'TRIAL_EXPIRED_RENEW' ? 'Renew License' : 'Activate License') }
}
